import { jwtDecode } from 'jwt-decode';
import { getAuthFromLocalStorage } from './authUtils';
import { getTokenRefreshHandler, getLoginRoute, getCurrentProvider } from './bbAxios';
import { clearStorageKeys, clearStorageByPattern } from './storageUtils';
import { STORAGE_KEYS } from '../sessionConfig';

/**
 * Check if a token is expired or about to expire
 * @param {string} token - The JWT access token
 * @param {number} bufferSeconds - Buffer time before actual expiry (default 30 seconds)
 * @returns {boolean} True if the token is expired or will expire within the buffer time
 */
const isTokenExpired = (token, bufferSeconds = 30) => {
  if (!token) return true;

  try {
    const decoded = jwtDecode(token);
    if (!decoded?.exp) return true;

    const expiresAtMs = decoded.exp * 1000;
    const now = Date.now();
    const bufferMs = bufferSeconds * 1000;

    return now >= expiresAtMs - bufferMs;
  } catch (error) {
    console.error('[fetchA] Error decoding token:', error);
    return true;
  }
};

/**
 * Function to handle OIDC authentication with fetch.
 * It retrieves the access token from local storage and adds it to the request headers.
 * Automatically refreshes expired tokens using the same handlers as bbAxios.
 *
 * @param {string} url - The URL to fetch.
 * @param {Object} [options={}] - The options for the fetch request,
 * including method, body, and headers.
 * @returns {Promise<Response>} The response from the fetch request.
 */
export default async function fetchA(url, options = {}) {
  const headers = new Headers(options.headers);
  const isFormDataBody =
    typeof FormData !== 'undefined' && options.body instanceof FormData;

  if (!headers.has('content-type') && !isFormDataBody) {
    headers.set('Content-Type', 'application/json');
  }

  let accessToken;
  if (getCurrentProvider() === 'gcp') {
    accessToken = localStorage.getItem(STORAGE_KEYS.GCP_TOKEN);
  } else {
    const oidcAuth = getAuthFromLocalStorage();
    accessToken = oidcAuth?.access_token;
  }

  // Check if token is expired and attempt refresh
  if (accessToken && isTokenExpired(accessToken)) {
    console.log('[fetchA] Token expired or expiring, attempting refresh before request');
    try {
      const refreshHandler = getTokenRefreshHandler();
      if (refreshHandler) {
        const newToken = await refreshHandler();
        if (newToken) {
          accessToken = newToken;
        }
      }
    } catch (refreshError) {
      console.error('[fetchA] Token refresh failed:', refreshError);
      // Redirect to login on refresh failure, preserving current path for post-login redirect
      clearStorageKeys(Object.values(STORAGE_KEYS));
      clearStorageByPattern('oidc.');
      clearStorageByPattern('@@auth0spajs@@');
      const loginRoute = getLoginRoute();
      const returnUrl = window.location.pathname + window.location.search;
      const separator = loginRoute.includes('?') ? '&' : '?';
      window.location.href = `${loginRoute}${separator}redirect=${encodeURIComponent(returnUrl)}`;
      throw refreshError;
    }
  }

  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  return fetch(url, { ...options, headers });
}
